import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

/* useRealtime — the client half of the sales-order real-time system
 * (mandate 2026-07-05). Opens an authenticated WebSocket to /api/ws, keeps the
 * unread notification badge live, plays a sound alarm on each incoming event,
 * and exposes the notification feed + mark-read actions. Auto-reconnects with
 * backoff; falls back to polling if the socket can't stay up. */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = () => {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  // Production builds ship with an EMPTY base (same-origin behind nginx) — the
  // socket must then derive from the page origin (wss on https).
  if (!base) {
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    return `${proto}://${window.location.host}/api/ws`;
  }
  const u = base.replace(/^http/, "ws");
  return `${u}/api/ws`;
};
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("lhc_token") || ""}` } });

// A short, synth two-tone chime via WebAudio — no asset file, respects autoplay
// (only fires after a user gesture has unlocked audio, which login guarantees).
let _audioCtx = null;
function playAlarm() {
  try {
    _audioCtx = _audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;
    [880, 1320].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const t0 = now + i * 0.16;
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(0.18, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.32);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(t0); osc.stop(t0 + 0.34);
    });
  } catch (e) { /* audio unavailable — badge still updates */ }
}

/* Softer two-note chime for chat messages (distinct from the order alarm). */
function playChatChime() {
  try {
    _audioCtx = _audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;
    [660, 880].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const t0 = now + i * 0.11;
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(0.09, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.22);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(t0); osc.stop(t0 + 0.24);
    });
  } catch (e) { /* audio unavailable */ }
}

/* Browser (OS-level) notification — best-effort, only when the tab is hidden. */
function browserNotify(title, body) {
  try {
    if (!("Notification" in window) || Notification.permission !== "granted") return;
    if (!document.hidden) return;
    const n = new Notification(title, { body, silent: true });
    setTimeout(() => n.close(), 8000);
  } catch (e) { /* not supported */ }
}

/* Ask once for browser-notification permission (call from a user-gesture path). */
export function requestBrowserNotifications() {
  try {
    if ("Notification" in window && Notification.permission === "default") Notification.requestPermission();
  } catch (e) { /* not supported */ }
}

export function useRealtime(user) {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null); // {type, orderId, ts} — drives list refreshes
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const aliveRef = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const [feed, cnt] = await Promise.all([
        axios.get(`${API}/notifications?limit=50`, authHeaders()).then((r) => r.data).catch(() => []),
        axios.get(`${API}/notifications/unread-count`, authHeaders()).then((r) => r.data).catch(() => ({ count: 0 })),
      ]);
      setNotifications(Array.isArray(feed) ? feed : []);
      setUnread((cnt && (cnt.count ?? cnt.unread)) || (Array.isArray(feed) ? feed.filter((n) => !n.read).length : 0));
    } catch (e) { /* keep last known */ }
  }, []);

  const markRead = useCallback(async (id) => {
    try { await axios.post(`${API}/notifications/${id}/read`, {}, authHeaders()); } catch (e) { /**/ }
    setNotifications((ns) => ns.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
  }, []);

  const markAllRead = useCallback(async () => {
    try { await axios.post(`${API}/notifications/read-all`, {}, authHeaders()); } catch (e) { /**/ }
    setNotifications((ns) => ns.map((n) => ({ ...n, read: true })));
    setUnread(0);
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    aliveRef.current = true;
    refresh();

    const connect = () => {
      if (!aliveRef.current) return;
      const token = localStorage.getItem("lhc_token");
      if (!token) return;
      let ws;
      try { ws = new WebSocket(`${WS_URL()}?token=${encodeURIComponent(token)}`); }
      catch (e) { scheduleReconnect(); return; }
      wsRef.current = ws;
      ws.onopen = () => { setConnected(true); retryRef.current = 0; };
      ws.onmessage = (ev) => {
        let msg = null;
        try { msg = JSON.parse(ev.data); } catch (e) { return; }
        if (msg.type === "connected") return;
        if (msg.type === "chat") {
          // order-chat ping — refresh open panels + soft chime + browser notif.
          // The backend never pings the author, so this is always "their" message.
          playChatChime();
          browserNotify(
            `${msg.from || "New message"} — ${msg.number || "order chat"}`,
            msg.preview || (msg.files ? "📎 Sent an attachment" : "New message"));
          setLastEvent({ type: "chat", orderId: msg.orderId, ts: Date.now() });
          return;
        }
        if (msg.type === "notification") {
          if (msg.sound) playAlarm();
          setUnread((u) => u + 1);
          setNotifications((ns) => [{
            id: "live-" + Date.now(), title: msg.title, body: msg.body,
            event_type: msg.event, entity_id: msg.orderId, read: false,
            created_at: new Date().toISOString(), _live: true,
          }, ...ns].slice(0, 60));
          setLastEvent({ type: msg.event, orderId: msg.orderId, ts: Date.now() });
          // reconcile with the server record shortly after
          setTimeout(refresh, 400);
        }
      };
      ws.onclose = () => { setConnected(false); scheduleReconnect(); };
      ws.onerror = () => { try { ws.close(); } catch (e) { /**/ } };
    };
    const scheduleReconnect = () => {
      if (!aliveRef.current) return;
      const delay = Math.min(15000, 800 * Math.pow(2, retryRef.current++));
      setTimeout(connect, delay);
    };
    connect();

    // keepalive ping + a slow poll as a safety net if the socket is down
    const ka = setInterval(() => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) { try { ws.send("ping"); } catch (e) { /**/ } }
      else refresh();
    }, 25000);

    return () => {
      aliveRef.current = false;
      clearInterval(ka);
      try { wsRef.current && wsRef.current.close(); } catch (e) { /**/ }
    };
  }, [user, refresh]);

  return { notifications, unread, connected, lastEvent, refresh, markRead, markAllRead };
}
